<template>
  <div>
    <v-card class="pa-5 mb-4" elevation="1">
      <v-card-title class="px-0 pt-0">Dashboard</v-card-title>
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

      <Row v-else>
        <Col
          v-for="card in totalCards"
          :key="card.key"
          cols="12"
          sm="6"
          md="4"
          lg="2"
        >
          <v-card outlined class="pa-4 text-center stat-card">
            <v-icon :color="card.color" size="32" class="mb-2">{{ card.icon }}</v-icon>
            <div class="text-h5 font-weight-bold">{{ card.value }}</div>
            <div class="text-caption text--secondary">{{ card.label }}</div>
          </v-card>
        </Col>
      </Row>
    </v-card>

    <Row v-if="!loading">
      <Col cols="12" md="4">
        <v-card class="pa-5" elevation="1" height="100%">
          <v-card-title class="px-0 pt-0">Pagamentos — semana</v-card-title>
          <PaymentPeriodSummary :summary="payments.week" />
        </v-card>
      </Col>
      <Col cols="12" md="4">
        <v-card class="pa-5" elevation="1" height="100%">
          <v-card-title class="px-0 pt-0">Pagamentos — mês</v-card-title>
          <PaymentPeriodSummary :summary="payments.month" />
        </v-card>
      </Col>
      <Col cols="12" md="4">
        <v-card class="pa-5" elevation="1" height="100%">
          <v-card-title class="px-0 pt-0">Pagamentos — ano</v-card-title>
          <PaymentPeriodSummary :summary="payments.year" />
        </v-card>
      </Col>
    </Row>

    <Row v-if="!loading && projection" class="mt-4">
      <Col cols="12">
        <AdminDashboardProjection
          :projection="projection"
          @change-compare-year="onCompareYearChange"
        />
      </Col>
    </Row>

    <Row v-if="!loading" class="mt-4">
      <Col cols="12">
        <v-card class="pa-5" elevation="1">
          <v-card-title class="px-0 pt-0 d-flex align-center flex-wrap">
            Pagamentos por mês
            <v-spacer />
            <v-select
              v-model="selectedYear"
              :items="availableYears"
              label="Ano"
              dense
              outlined
              hide-details
              class="dashboard-year-select mt-2 mt-sm-0"
              @change="fetchDashboard"
            />
          </v-card-title>
          <apexchart
            type="bar"
            height="320"
            :options="monthChartOptions"
            :series="monthChartSeries"
          />
        </v-card>
      </Col>
    </Row>

    <Row v-if="!loading" class="mt-4">
      <Col cols="12">
        <v-card class="pa-5" elevation="1">
          <v-card-title class="px-0 pt-0">Pagamentos por ano</v-card-title>
          <apexchart
            v-if="yearChartData.length"
            type="bar"
            height="320"
            :options="yearChartOptions"
            :series="yearChartSeries"
          />
          <div v-else class="text-center py-8 text--secondary">
            Nenhum pagamento encontrado.
          </div>
        </v-card>
      </Col>
    </Row>

    <Row v-if="!loading" class="mt-4">
      <Col cols="12">
        <v-card class="pa-5" elevation="1">
          <v-card-title class="px-0 pt-0">Assinaturas por status</v-card-title>
          <apexchart
            v-if="subscriptionChartSeries.length"
            type="donut"
            height="320"
            :options="subscriptionChartOptions"
            :series="subscriptionChartSeries"
          />
          <div v-else class="text-center py-8 text--secondary">
            Nenhuma assinatura encontrada.
          </div>
        </v-card>
      </Col>
    </Row>
  </div>
</template>

<script>
import { Row, Col } from '@/components/vuetify';
import { formatCurrency } from '@/utils';
import PaymentPeriodSummary from './components/AdminDashboardPaymentPeriodSummary.vue';
import AdminDashboardProjection from './components/AdminDashboardProjection.vue';

const SUBSCRIPTION_STATUS_LABELS = {
  active: 'Ativa',
  pending: 'Pendente',
  waiting_payment: 'Aguardando pagamento',
  suspended: 'Suspensa',
  canceled: 'Cancelada',
  finished: 'Finalizada',
};

export default {
  name: 'AdminDashboard',
  components: {
    Row,
    Col,
    PaymentPeriodSummary,
    AdminDashboardProjection,
  },
  data() {
    return {
      loading: false,
      selectedYear: new Date().getFullYear(),
      compareYear: new Date().getFullYear() - 1,
      availableYears: [new Date().getFullYear()],
      projection: null,
      totals: {
        customers: 0,
        companies: 0,
        subscriptions: 0,
        resellers: 0,
        distributors: 0,
      },
      subscriptionsByStatus: {},
      payments: {
        week: {},
        month: {},
        year: {},
        by_month: [],
        by_year: [],
      },
    };
  },
  computed: {
    totalCards() {
      return [
        { key: 'customers', label: 'Clientes', value: this.totals.customers, icon: 'mdi-account-group', color: 'primary' },
        { key: 'companies', label: 'Empresas', value: this.totals.companies, icon: 'mdi-domain', color: 'indigo' },
        { key: 'subscriptions', label: 'Assinaturas', value: this.totals.subscriptions, icon: 'mdi-card-account-details', color: 'teal' },
        { key: 'resellers', label: 'Revendedores', value: this.totals.resellers, icon: 'mdi-storefront', color: 'orange' },
        { key: 'distributors', label: 'Distribuidores', value: this.totals.distributors, icon: 'mdi-truck', color: 'deep-purple' },
      ];
    },
    monthChartData() {
      return this.payments.by_month || [];
    },
    yearChartData() {
      return this.payments.by_year || [];
    },
    monthChartSeries() {
      return [
        {
          name: 'Valor pago',
          data: this.monthChartData.map((item) => Number(item.paid_amount || 0)),
        },
      ];
    },
    yearChartSeries() {
      return [
        {
          name: 'Valor pago',
          data: this.yearChartData.map((item) => Number(item.paid_amount || 0)),
        },
      ];
    },
    monthChartOptions() {
      return this.buildBarChartOptions(this.monthChartData);
    },
    yearChartOptions() {
      return this.buildBarChartOptions(this.yearChartData);
    },
    subscriptionChartSeries() {
      return Object.values(this.subscriptionsByStatus).map((value) => Number(value));
    },
    subscriptionChartOptions() {
      return {
        labels: Object.keys(this.subscriptionsByStatus).map(
          (status) => SUBSCRIPTION_STATUS_LABELS[status] || status
        ),
        legend: { position: 'bottom' },
        colors: ['#4caf50', '#ff9800', '#2196f3', '#f44336', '#9e9e9e', '#673ab7'],
      };
    },
  },
  mounted() {
    this.fetchDashboard();
  },
  methods: {
    buildBarChartOptions(chartData) {
      return {
        chart: {
          toolbar: { show: false },
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '55%',
          },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: chartData.map((item) => item.label),
        },
        yaxis: {
          labels: {
            formatter: (value) => formatCurrency(value),
          },
        },
        tooltip: {
          y: {
            formatter: (value) => formatCurrency(value),
          },
        },
        colors: ['#292f4c'],
        legend: { show: false },
      };
    },
    fetchDashboard() {
      this.loading = true;

      this.$api.admin
        .getDashboard({
          year: this.selectedYear,
          compareYear: this.compareYear,
        })
        .then(({ data }) => {
          this.totals = data.totals || this.totals;
          this.subscriptionsByStatus = data.subscriptions_by_status || {};
          this.payments = data.payments || this.payments;
          this.projection = data.projection || null;
          this.availableYears = data.payments?.available_years || this.availableYears;
          this.selectedYear = data.payments?.selected_year || this.selectedYear;
          this.compareYear = data.projection?.compare_year || this.compareYear;
        })
        .catch(() => {
          this.$noty.error('Erro ao carregar o dashboard');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onCompareYearChange(compareYear) {
      this.compareYear = compareYear;
      this.fetchDashboard();
    },
  },
};
</script>

<style scoped>
.stat-card {
  min-height: 130px;
}

.dashboard-year-select {
  max-width: 120px;
}
</style>
